/**
 * @module layoutProof
 *
 * Proves, from two binary schemas alone, that a FlatBuffer type is laid out
 * identically in both: a buffer written with one reads the same with the
 * other.
 *
 * The proof is deliberately narrow. A type passes only when it and everything
 * reachable from it (nested tables and structs, vector elements, unions and
 * their members, enums) agree on every detail that decides where a value sits
 * in the buffer or what a number means. Any difference at all, including
 * ones that would in fact be harmless (a field added at the end, a new enum
 * value), fails it. A schema the page cannot prove stays unproven; it is
 * never guessed compatible.
 */

import { ReflectionBaseType } from "../reflection/reflection";

/** The part of a schema type that decides how a value is stored. */
export interface LayoutType {
  baseType(): ReflectionBaseType;
  element(): ReflectionBaseType;
  index(): number;
  fixedLength(): number;
  baseSize(): number;
  elementSize(): number;
}

/** One field of a table or struct. */
export interface LayoutField {
  name(): string;
  type(): LayoutType | null;
  offset(): number;
  id(): number;
  required(): boolean;
  deprecated(): boolean;
  key(): boolean;
  optional(): boolean;
  defaultInteger(): bigint;
  defaultReal(): number;
}

/** A table or struct. */
export interface LayoutObject {
  name(): string;
  isStruct(): boolean;
  byteSize(): number;
  minAlign(): number;
  fields(): LayoutField[];
}

/** One value of an enum or one member of a union. */
export interface LayoutEnumVal {
  name(): string;
  value(): bigint;
  unionType(): LayoutType | null;
}

/** An enum or a union. */
export interface LayoutEnum {
  name(): string;
  isUnion(): boolean;
  underlyingType(): LayoutType | null;
  values(): LayoutEnumVal[];
}

/** A binary schema, as far as the proof reads it. */
export interface LayoutSchema {
  objects(): LayoutObject[];
  enums(): LayoutEnum[];
  objectByName(name: string): LayoutObject | undefined;
  enumByName(name: string): LayoutEnum | undefined;
}

/** What a type's index points at, by name, since indices differ between schemas. */
interface Reference {
  kind: "object" | "enum";
  name: string;
}

/** One schema's definitions, read once. */
interface View {
  schema: LayoutSchema;
  objects: readonly LayoutObject[];
  enums: readonly LayoutEnum[];
}

/** Definitions reached but not yet compared, and those already queued. */
interface Work {
  objects: string[];
  enums: string[];
  queuedObjects: Set<string>;
  queuedEnums: Set<string>;
}

function view(schema: LayoutSchema): View {
  return { schema, objects: schema.objects(), enums: schema.enums() };
}

function queue(work: Work, target: Reference): void {
  if (target.kind === "object") {
    if (!work.queuedObjects.has(target.name)) {
      work.queuedObjects.add(target.name);
      work.objects.push(target.name);
    }
  } else if (!work.queuedEnums.has(target.name)) {
    work.queuedEnums.add(target.name);
    work.enums.push(target.name);
  }
}

function isContainer(base: ReflectionBaseType): boolean {
  return (
    base === ReflectionBaseType.Vector ||
    base === ReflectionBaseType.Vector64 ||
    base === ReflectionBaseType.Array
  );
}

/**
 * What `index` names when it belongs to a value of type `base`: an object for
 * `Obj`, an enum for a union, a union tag or an integer typed by an enum, and
 * nothing for the rest. `undefined` when the index points outside the schema.
 */
function reference(
  schema: View,
  base: ReflectionBaseType,
  index: number,
): Reference | null | undefined {
  if (base === ReflectionBaseType.Obj) {
    const object = schema.objects[index];
    return object === undefined ? undefined : { kind: "object", name: object.name() };
  }
  if (index < 0) {
    return null;
  }
  const definition = schema.enums[index];
  return definition === undefined ? undefined : { kind: "enum", name: definition.name() };
}

/** The base type whose `index` a type carries: its element for a vector or array. */
function carrier(type: LayoutType): ReflectionBaseType {
  const base = type.baseType();
  return isContainer(base) ? type.element() : base;
}

/** Whether two references name the same definition (or both nothing); queues it when they do. */
function sameReference(
  a: Reference | null | undefined,
  b: Reference | null | undefined,
  work: Work,
): boolean {
  if (a === undefined || b === undefined) {
    return false;
  }
  if (a === null || b === null) {
    return a === b;
  }
  if (a.kind !== b.kind || a.name !== b.name) {
    return false;
  }
  queue(work, a);
  return true;
}

function sameType(
  a: LayoutType | null,
  b: LayoutType | null,
  views: { writer: View; reader: View },
  work: Work,
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  const base = a.baseType();
  const same =
    base === b.baseType() &&
    a.element() === b.element() &&
    a.fixedLength() === b.fixedLength() &&
    a.baseSize() === b.baseSize() &&
    a.elementSize() === b.elementSize();
  if (!same) {
    return false;
  }
  const kind = carrier(a);
  return sameReference(
    reference(views.writer, kind, a.index()),
    reference(views.reader, kind, b.index()),
    work,
  );
}

function sameField(
  a: LayoutField,
  b: LayoutField,
  views: { writer: View; reader: View },
  work: Work,
): boolean {
  return (
    a.id() === b.id() &&
    a.offset() === b.offset() &&
    a.required() === b.required() &&
    a.deprecated() === b.deprecated() &&
    a.key() === b.key() &&
    a.optional() === b.optional() &&
    a.defaultInteger() === b.defaultInteger() &&
    Object.is(a.defaultReal(), b.defaultReal()) &&
    sameType(a.type(), b.type(), views, work)
  );
}

function sameObject(
  a: LayoutObject,
  b: LayoutObject,
  views: { writer: View; reader: View },
  work: Work,
): boolean {
  if (
    a.isStruct() !== b.isStruct() ||
    a.byteSize() !== b.byteSize() ||
    a.minAlign() !== b.minAlign()
  ) {
    return false;
  }
  const writerFields = a.fields();
  const readerFields = new Map(b.fields().map((field) => [field.name(), field]));
  if (writerFields.length !== readerFields.size) {
    return false;
  }
  return writerFields.every((field) => {
    const counterpart = readerFields.get(field.name());
    return counterpart !== undefined && sameField(field, counterpart, views, work);
  });
}

function sameEnum(
  a: LayoutEnum,
  b: LayoutEnum,
  views: { writer: View; reader: View },
  work: Work,
): boolean {
  if (a.isUnion() !== b.isUnion()) {
    return false;
  }
  const underlying = a.underlyingType();
  const otherUnderlying = b.underlyingType();
  if (underlying === null || otherUnderlying === null) {
    if (underlying !== otherUnderlying) {
      return false;
    }
  } else if (underlying.baseType() !== otherUnderlying.baseType()) {
    return false;
  }
  const writerValues = a.values();
  const readerValues = new Map(b.values().map((value) => [value.name(), value]));
  if (writerValues.length !== readerValues.size) {
    return false;
  }
  return writerValues.every((value) => {
    const counterpart = readerValues.get(value.name());
    return (
      counterpart !== undefined &&
      value.value() === counterpart.value() &&
      sameType(value.unionType(), counterpart.unionType(), views, work)
    );
  });
}

/**
 * Whether `typeName` (fully qualified, e.g. `Api.PaginatedBanks`) and
 * everything reachable from it are laid out identically in `writer` and
 * `reader`. False when either schema lacks a definition the walk reaches, when
 * an index points outside its schema, or on any difference.
 */
export function sameReachableLayout(
  writer: LayoutSchema,
  reader: LayoutSchema,
  typeName: string,
): boolean {
  const views = { writer: view(writer), reader: view(reader) };
  const work: Work = {
    objects: [typeName],
    enums: [],
    queuedObjects: new Set([typeName]),
    queuedEnums: new Set(),
  };
  while (work.objects.length > 0 || work.enums.length > 0) {
    const objectName = work.objects.pop();
    if (objectName !== undefined) {
      const a = writer.objectByName(objectName);
      const b = reader.objectByName(objectName);
      if (a === undefined || b === undefined || !sameObject(a, b, views, work)) {
        return false;
      }
    }
    const enumName = work.enums.pop();
    if (enumName !== undefined) {
      const a = writer.enumByName(enumName);
      const b = reader.enumByName(enumName);
      if (a === undefined || b === undefined || !sameEnum(a, b, views, work)) {
        return false;
      }
    }
  }
  return true;
}
